import { getURLSearchParamsFromQuery } from '@schibsted-nmp/search-frontend-clients';
import type { SearchKey } from '@schibsted-nmp/search-frontend-clients/quest';
import {
    useQuery,
    useQueryClient,
    type QueryClient,
    type QueryFunctionContext,
} from '@tanstack/react-query';
import type { PolePosition } from '../../server';
import { useAppConfig } from '../AppConfig';
import { queryKeys, type SearchParams } from './QueryKeys';

interface PolePositionQueryMeta {
    apiUrl: string;
    queryClient: QueryClient;
}

declare module '@tanstack/react-query' {
    type QueryMeta = PolePositionQueryMeta;
}

export function usePolePositionData(
    searchKey: SearchKey,
    searchParams: SearchParams,
) {
    const { apiUrl } = useAppConfig();
    const queryClient = useQueryClient();

    const polePositionResult = useQuery({
        meta: { apiUrl, queryClient },
        queryFn: fetchPolePositionData,
        queryKey: queryKeys.polePosition({ searchKey, searchParams }),
        ...getQueryCacheOptions(),
    });

    return {
        polePositionData: polePositionResult.data,
    };
}

function getQueryCacheOptions() {
    // These options combined will make sure that we have referential equality
    // for the current data between renders, as long as the query doesn't
    // change. We need referential equality to ensure that hooks or components
    // using the data as a dependency don't update unless the query has changed.

    const keepPreviousData = <T>(previousData: T) => previousData;

    return {
        // Keep the current data while fetching data for a new query.
        placeholderData: keepPreviousData,
        // Never mark cached data as stale, to avoid refetching data for the
        // current query.
        staleTime: Infinity,
        // Garbage collect cached data immediately, to force refetching data for
        // new queries.
        gcTime: 0,
    };
}

type PolePositionQueryKey = ReturnType<typeof queryKeys.polePosition>;

async function fetchPolePositionData({
    meta,
    signal,
    queryKey,
}: QueryFunctionContext<PolePositionQueryKey>): Promise<PolePosition> {
    const { apiUrl } = meta ?? {};
    const [{ searchKey, searchParams }] = queryKey;

    if (searchKey === null) {
        return Promise.reject(
            new Error('Cannot fetch pole position without searchKey'),
        );
    }

    const urlSearchParams = getURLSearchParamsFromQuery(searchParams);

    const url = new URL(`${apiUrl}/pole-position/${searchKey}`);
    url.search = urlSearchParams.toString();

    try {
        const response = await fetch(url, {
            headers: {
                Accept: 'application/json',
            },
            signal,
        });
        if (!response.ok) {
            const message = await response.text();
            return Promise.reject(
                new Error(
                    `Got status ${response.status} calling ${url}: ${message}`,
                ),
            );
        }
        removePreviousQueries(meta?.queryClient as QueryClient | undefined);
        return response.json();
    } catch (err) {
        return Promise.reject(
            new Error(`Error calling ${url}`, { cause: err }),
        );
    }
}

function removePreviousQueries(queryClient?: QueryClient) {
    // This is a workaround for removing just the initial server-side rendered
    // search query, and not the subsequent client-side queries. This is
    // necessary because the server-side rendered query is not garbage collected
    // and removed from the cache.

    queryClient?.removeQueries({
        type: 'inactive',
    });
}
